import * as React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {
  ClickAwayListener,
  Typography,
  Popover,
  FormControl,
  InputAdornment,
  Input,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { StyledFormControl } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import { debounce } from "lodash";
import {
  BodyLarge,
  BodySmall,
  H6HeadlineSmall,
  H7TitleLarge,
} from "../../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
} from "../../../../components/StyledComponents/Buttons/AuradineButtons";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useEffect, useState } from "react";
import useMinerPageContext from "../../../context/useMinerPageContext.js";
import { useGetTagsList } from "../../../../api/queries/useGetTagsList.ts";
import TagList from "../../../../TagMangement/Components/TagList/TagList.js";
import { usePostAttachTagsToMiners, usePostAttachTagsToMinersCS } from "../../../queries/usePostAttachTagsToMiners.ts";
import { useQueryClient } from "@tanstack/react-query";
import { StyledTextField } from "../../../../components/StyledComponents/Inputs/Inputs.js";
import { useTheme } from "../../../../ThemeContext/ThemeContext.js";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  flexDirection: "column",
  background: colors?.background?.popover,
  borderRadius: "1rem",
  boxShadow: colors?.boxShadow?.card || "0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const StyledPopover = styled(Popover)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem", // Change this to the border radius you want
    backgroundColor: colors?.background?.popover,
    boxShadow:
      colors?.boxShadow?.popover ||
      "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
  },
}));

const StyledHeaderAndFooterBox = styled(Box)`
  padding: 1.5rem;
`;

const TagOperationsCard = (props) => {
  const { open, handleClose, anchorRef, refetch, newTag, setNewTag, setTableKey } = props;
  const [searchText, setSearchText] = useState("");
  const [dSearchText, setDSearchText] = useState("");
  const history = useHistory();
  const [authToken, setAuthToken] = React.useState("");
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { colors } = useTheme();
  useEffect(() => {
    console.log(1);
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const debouncedUpdate = React.useMemo(
    () =>
      debounce((text) => {
        setDSearchText(text); // Update the search text state
      }, 300), // 300ms debounce delay
    []
  );

  const handleSearchChange = (event) => {
    const text = event.target.value;
    setSearchText(text);
    debouncedUpdate(text); // Trigger the debounced function
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: tagsDataRefetch,
    status,
    isFetched: isTagsDataFetched,
    isLoading: isTagsDataLoading,
  } = useGetTagsList({
    authToken,
    tagName: dSearchText,
  });

  let limit = 25;
  let [tagData, setTagData] = useState([]);
  const { selectedSerials, setSelectedSerials, setUntagMinersDialogOpen } = useMinerPageContext();

  useEffect(() => {
    if (data && data?.pages) {
      const tagData =
        isTagsDataFetched && data?.pages.length > 0
          ? data?.pages
              .flatMap((page, pageIndex) =>
                page.msg.map((item, index) => {
                  // Generate a unique ID by combining the page index and the item index
                  return {
                    ...item,
                    id: pageIndex * limit + index,
                    isTagSelected: false,
                  };
                })
              )
              .sort((a, b) => {
                return a.tag.localeCompare(b.tag);
              })
          : [];
      setTagData((prevData) => {
        const finalData = tagData.map((item) => {
          return {
            ...item,
            isTagSelected:
              prevData.find((prevItem) => prevItem.tag === item.tag)
                ?.isTagSelected || false,
          };
        });
        return finalData;
      });
    }
  }, [data, isTagsDataFetched, limit]);

  const queryClient = useQueryClient();
  const { mutate: attachTagsToMinersCS } = usePostAttachTagsToMinersCS();

  const handleAttachTags = async () => {
    try {
      const minernames = selectedSerials.map(serial => ({ SerialNo: serial, CSTag: newTag }));
      const payload = {
        minernames: minernames,
      };
      console.log("PAYLOAD", payload);
      await attachTagsToMinersCS(payload,{
        onSuccess: async (data) => {
          if (data?.status === "ok") {
            const tagsData = tagData.map((tag) =>
              tag.isTagSelected
                ? { ...tag, isTagSelected: false }
                : { ...tag }
            );
            setTagData(tagsData);
            await refetch();
            setTableKey((prevKey) => prevKey + 1);
            setSelectedSerials([]);
          }
        },
        onError: (error) => {
          console.error("Error attaching tags:", error);
        },
      });
    } catch (error) {
      console.error("Error attaching tags:", error);
    } finally {
      handleClose();
    }
  };
  return (
    <StyledPopover
      colors={colors}
      sx={{ zIndex: 9999 }}
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <StyledPopoverContent colors={colors}>
          <StyledHeaderAndFooterBox
            display={"flex"}
            flexDirection={"column"}
            gap={4}
          >
            <Box
              display="flex"
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <H7TitleLarge>Add tag:</H7TitleLarge>
              <SecondaryButton colors={colors}
                onClick={handleAttachTags}
                endIcon={
                  <span className="material-symbols-outlined">new_label</span>
                }
              >
                Add CS Tag
              </SecondaryButton>
            </Box>
          </StyledHeaderAndFooterBox>
          <Box
            display="flex"
            flexDirection={"column"}
            px="1.5rem"
            pb="1.5rem"
            className={"search-box"}
          >
            <StyledTextField colors={colors}
              value={newTag}
              onChange={(event) => setNewTag(event.target.value)}
              placeholder="Add New CS Tag"
            ></StyledTextField>
          </Box>
          <Box>
          </Box>
        </StyledPopoverContent>
      </ClickAwayListener>
    </StyledPopover>
  );
};

export default TagOperationsCard;

export const TagOperationsTab = (props) => {
  const { refetch: minerPageRefetch, setTableKey } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { user } = useAuth0();
  const { selectedSerials } = useMinerPageContext();
  const [newTag, setNewTag] = useState("");
  const { colors } = useTheme();
  // Assuming this is coming from context or props
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setNewTag("");
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          // paddingRight: "2rem",
        }}
      >
        <SecondaryButton colors={colors}
          ref={anchorRef}
          onClick={handleClick}
          disabled={selectedSerials.length === 0}
          endIcon={<span className="material-symbols-outlined">new_label</span>}
        >
          Support Tag
        </SecondaryButton>
      </Box>

      <TagOperationsCard
        open={open}
        handleClose={handleClose}
        anchorRef={anchorRef}
        refetch={minerPageRefetch}
        newTag={newTag}
        setTableKey={setTableKey}
        setNewTag={setNewTag}
      />
    </React.Fragment>
  );
};
